import { create } from 'zustand';
import { useCallback } from 'react';
import type { GameId, GameInfoResponse } from '../api/types';
import type { ApiError } from '../api/client';
import { friendlyErrorMessage } from '../utils/errorCopy';

export type GetGameInfo = (gameId: GameId, recent?: number) => Promise<GameInfoResponse>;

export interface GameInfoState {
  loading: boolean;
  error: string | null;
  dataByGameId: Partial<Record<GameId, GameInfoResponse>>;
  load: (gameId: GameId, deps: { getGameInfo: GetGameInfo }, recent?: number) => Promise<void>;
  clearError: () => void;
}

/**
 * Store is dependency-injected so we can test it without Clerk hooks.
 * App code passes `useGameApi().getGameInfo` into `load()`.
 */
export const useGameInfoStore = create<GameInfoState>((set, get) => ({
  loading: false,
  error: null,
  dataByGameId: {},
  clearError: () => set({ error: null }),
  load: async (gameId, deps, recent = 20) => {
    const existing = get().dataByGameId[gameId];
    set({ loading: true, error: null });
    try {
      const res = await deps.getGameInfo(gameId, recent);
      set((prev) => ({
        loading: false,
        error: null,
        dataByGameId: { ...prev.dataByGameId, [gameId]: res },
      }));
    } catch (err) {
      const msg =
        isApiError(err) && err.code === 'unauthorized'
          ? 'Please sign in to load game data.'
          : friendlyErrorMessage(err, 'Failed to load game data');
      set((prev) => ({
        loading: false,
        error: msg,
        dataByGameId: existing ? prev.dataByGameId : prev.dataByGameId,
      }));
    }
  },
}));

export function useGameInfo(gameId: GameId): {
  loading: boolean;
  error: string | null;
  data: GameInfoResponse | undefined;
  load: (deps: { getGameInfo: GetGameInfo }, recent?: number) => Promise<void>;
  clearError: () => void;
} {
  const loading = useGameInfoStore((s) => s.loading);
  const error = useGameInfoStore((s) => s.error);
  const data = useGameInfoStore((s) => s.dataByGameId[gameId]);
  const baseLoad = useGameInfoStore((s) => s.load);
  const clearError = useGameInfoStore((s) => s.clearError);

  const load = useCallback(
    async (deps: { getGameInfo: GetGameInfo }, recent?: number) => {
      await baseLoad(gameId, deps, recent);
    },
    [baseLoad, gameId]
  );

  return { loading, error, data, load, clearError };
}

function isApiError(err: unknown): err is ApiError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    (err as { name?: unknown }).name === 'ApiError' &&
    'code' in err
  );
}
